import React, { Component, useRef, useState, useLayoutEffect, useEffect } from "react";
import { render } from "react-dom";
import { withGoogleMap, GoogleMap, InfoWindow, Marker } from "react-google-maps";
import InfoBoxCustom from "~/components/InfoBoxCustom/InfoBoxCustom";
import styles from "./MapView.scss";
import classnames from "classnames";
import constants from "../../utils/constants";
import { CerrarIcon } from "../../theme/assets";
import { defineMessages, FormattedMessage, injectIntl } from "react-intl";

export const msgs = defineMessages({
  book: {
    id: "mapView.label.book",
    description: "Label to book",
    defaultMessage: "Book",
  },
  viewAvailability: {
    id: "mapView.label.viewAvailability",
    description: "Label to view availability",
    defaultMessage: "View availability",
  },
  viewOptions: {
    id: "mapView.label.viewOptions",
    description: "Label to view options",
    defaultMessage: "View options",
  },
});

const CenteredInfoBox = ({ children, options, ...otherProps }) => {
  const [dimensions, setDimensions] = useState(null);
  useLayoutEffect(() => {
    let targetRef;
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.visibility = "hidden";
    container.style.left = 0;
    container.style.top = 0;
    document.body.appendChild(container);
    const el = (
      <div className={classnames(styles.container, styles.forceVisible)}>
        <div ref={c => (targetRef = c)} className={options.boxClass}>
          {children}
        </div>
      </div>
    );
    render(el, container, () => {
      if (targetRef) {
        setDimensions({
          width: targetRef.offsetWidth,
          height: targetRef.offsetHeight,
        });
        try {
          document.body.removeChild(container);
        } catch (e) {
          console.info(e);
        }
      }
    });
  }, []);
  const realOptions = dimensions
    ? { pixelOffset: new google.maps.Size(-dimensions.width / 2, 0), ...options }
    : options;
  return (
    <InfoBoxCustom options={{ ...realOptions }} {...otherProps}>
      <div>{children}</div>
    </InfoBoxCustom>
  );
};

class MapView extends Component {
  constructor(props) {
    super(props);
  }

  render() {
    const mapLoaded = this.props.onMapLoaded;
    const {
      hotelSelected,
      onHover,
      markers,
      center,
      onMouseLeave,
      intl: { formatMessage } = {},
    } = this.props;
    // TODO: This window.wbk... variables ideally must be declared in the APP.js and be passed down as props
    const mapType = window.wbkMapType || "roadmap";
    const mapZoom = window.wbkMapZoom || 15;
    const centerLat = window.wbkMapCenterLat || center.lat;
    const centerLng = window.wbkMapCenterLng || center.lng;

    const removePoi = [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [
          {
            visibility: "off",
          },
        ],
      },
    ];

    return (
      <GoogleMap
        defaultCenter={{
          lat: centerLat,
          lng: centerLng,
        }}
        defaultZoom={mapZoom}
        mapTypeId={mapType}
        options={{ styles: window.wbkMapStyles || removePoi }}
        defaultOptions={{ styles: window.wbkMapStyles || removePoi }}
        ref={mapLoaded}>
        {markers.map((marker, index) => {
          if (marker.type !== "marker") {
            const onClick = this.props.onMarkerClick.bind(this, marker);
            const result = [];
            result.push(
              <CenteredInfoBox
                key={index + "_1"}
                options={{
                  closeBoxURL: ``,
                  alignBottom: true,
                  enableEventPropagation: true,
                  boxClass: "infoWindow",
                }}
                position={marker.position}>
                <div
                  className={classnames(
                    styles.infoWindowContent,
                    marker.showInfo ? styles.active : styles.inactive
                  )}>
                  <div className={styles.propertyInfo}>
                    {this.props.renderInfoWindowContent(marker, formatMessage, marker.lastStatus)}
                  </div>
                </div>
              </CenteredInfoBox>
            );
            result.push(
              <CenteredInfoBox
                key={index + "_2"}
                options={{
                  closeBoxURL: ``,
                  enableEventPropagation: true,
                  alignBottom: true,
                  boxClass: classnames(
                    "infoPrice",
                    hotelSelected === marker.ticker && "priceSelected",
                    {
                      [styles.isUnavailable]:
                        marker.lastStatus === constants.PROPERTY_STATUS.UNAVAILABLE ||
                        !marker.lastStatus,
                    }
                  ),
                }}
                position={marker.position}>
                <div
                  className={classnames(
                    styles.infoWindowContent,
                    marker.showInfo ? styles.active : styles.inactive
                  )}
                  onClick={onClick}
                  onMouseEnter={() => onHover(marker.ticker)}
                  onMouseLeave={onMouseLeave}>
                  <div
                    className={classnames(styles.propertyPrice, "wbkv9-MapView-propertyPrice", {
                      [styles.selected]: hotelSelected === marker.ticker,
                      [styles.available]: marker.lastStatus === constants.PROPERTY_STATUS.AVAILABLE,
                      "wbkv9-MapView-propertyPrice-Available":
                        marker.lastStatus === constants.PROPERTY_STATUS.AVAILABLE,
                      [styles.unavailable]:
                        marker.lastStatus === constants.PROPERTY_STATUS.UNAVAILABLE ||
                        !marker.lastStatus,
                      [styles.restricted]:
                        marker.lastStatus === constants.PROPERTY_STATUS.RESTRICTED,
                    })}>
                    {marker.rate}
                  </div>
                </div>
              </CenteredInfoBox>
            );
            return result;
          } else {
            return <Marker {...marker} />;
          }
        })}
      </GoogleMap>
    );
  }
}

const InjectedMapView = withGoogleMap(MapView);

const Container = props => {
  const [markers, setMarkers] = useState(props.initialMarkers);
  const [center, setCenter] = useState(props.initialCenter);
  const [showEmbedded, setShowEmbedded] = useState(props.initialMarkers.length === 1);
  const [stateMap, setMap] = useState(null);

  const mapLoaded = map => {
    if (!map) {
      return;
    }
    if (map === stateMap) {
      return;
    }

    window.wbkMap = map.props.map;
    if (window.wbkMap && window.google) {
      window.google.maps.event.trigger(window.wbkMap, "resize");
    }

    if (!window.wbkIgnoreFitBounds) {
      const bounds = new google.maps.LatLngBounds();
      markers.map(it => bounds.extend(it.position));
      map.fitBounds(bounds);
    }
    if (window.wbkOnMapLoaded) {
      window.wbkOnMapLoaded();
    }
    setMap(map);
  };

  const handleMarkerClick = (targetMarker, event) => {
    event.preventDefault();
    event.stopPropagation();
    setMarkers(
      markers.map(marker =>
        marker === targetMarker ? { ...marker, showInfo: true } : { ...marker, showInfo: false }
      )
    );
  };

  const handleMarkerClose = event => {
    setMarkers(markers.map(marker => ({ ...marker, showInfo: false })));
  };

  const renderInfoWindowContent = (marker, formatMessage, lastStatus) => {
    let bookMsg;
    let bookMsgClass = "";
    switch (lastStatus) {
      case constants.PROPERTY_STATUS.AVAILABLE:
        bookMsg = formatMessage(msgs.book);
        break;

      case constants.PROPERTY_STATUS.RESTRICTED:
        bookMsg = formatMessage(msgs.viewOptions);
        bookMsgClass = styles.unavailable;
        break;

      default:
        bookMsg = formatMessage(msgs.viewAvailability);
        bookMsgClass = styles.unavailable;
        break;
    }
    return (
      <div className={styles.infoContent}>
        <CerrarIcon className={styles.closeIcon} onClick={handleMarkerClose} />
        <h1 className={styles.title}>{marker.title}</h1>
        <div>{marker.content}</div>
        <span
          onClick={event => {
            handleMarkerClose(event);
            if (props.onPropertySelect) {
              props.onPropertySelect(marker.ticker);
            }
          }}
          className={classnames(styles.iwAction, bookMsgClass)}>
          {bookMsg}
        </span>
        <div className={styles.arrowDown}>
          <div className={styles.arrowDownLeft}>
            <div className={styles.arrowDownLeftContent} />
          </div>
          <div className={styles.arrowDownRight}>
            <div className={styles.arrowDownRightContent} />
          </div>
        </div>
      </div>
    );
  };

  const hideEmbedded = () => {
    setShowEmbedded(false);
  };

  useEffect(() => {
    if (window.wbkMap && props.initialMarkers) {
      const bounds = new google.maps.LatLngBounds();
      props.initialMarkers.map(it => bounds.extend(it.position));
      window.wbkMap.fitBounds(bounds);

      if (window.wbkMap.getZoom() > 18) {
        window.wbkMap.setZoom(15);
      }
    }
    setMarkers(props.initialMarkers);
  }, [props.initialMarkers]);

  if (showEmbedded && markers && !props.hasPremiumMap) {
    const mk = markers[0];
    const mapType = window.wbkMapType || "roadmap"; // replace 5e0 for 5e1 for satellite
    const mapZoom = window.wbkMapZoom || 15;
    const lat = mk.position.lat();
    const long = mk.position.lng();
    const mapScale = 591657550.5 / Math.pow(2, mapZoom + 1);
    const base64 = btoa([lat, long]); // lazy "long,lat" formatting
    const iframeSrc =
      "https://www.google.com/maps/embed?key=AIzaSyA9uk54hco4xzjMvGrSFKpuo-qgsUdwFSw&pb=!1m7!1m2!1m1!1d" +
      mapScale +
      "!3m3!1m2!1s0!2z" +
      base64;
    return (
      <div style={{ height: `100%` }} className={styles.container}>
        <div id="overMap" className={styles.iframeContainer} />
        <iframe
          width="100%"
          height="100%"
          frameBorder="0"
          style={{ border: "0" }}
          src={iframeSrc}
        />
      </div>
    );
  } else {
    return (
      <InjectedMapView
        onMapLoaded={mapLoaded}
        onMarkerClick={handleMarkerClick}
        onMarkerClose={handleMarkerClose}
        markers={markers}
        renderInfoWindowContent={renderInfoWindowContent}
        center={center}
        containerElement={
          <div style={{ height: `100%` }} className={styles.container}>
            {" "}
          </div>
        }
        mapElement={<div style={{ height: `100%`, overflow: "visible" }} />}
        {...props}
      />
    );
  }
};

export default injectIntl(Container);
